import React, { useState, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { X, Upload, Camera, Sparkles, CheckCircle2, AlertCircle, RefreshCw, FileText, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { parseReceiptText, SAMPLE_RECEIPTS } from 'shared/utils/receiptParser';
import { toast } from 'sonner';

export default function ReceiptScannerModal({ isOpen, onClose, onApplyReceipt }) {
  const { theme, isDark } = useTheme();
  const { formatCurrency, displayCurrency, getCurrencyInfo } = useCurrency();
  const currencyInfo = getCurrencyInfo(displayCurrency);

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  const [imagePreview, setImagePreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [extractedData, setExtractedData] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [rawText, setRawText] = useState('');

  if (!isOpen) return null;

  const resetState = () => {
    setImagePreview(null);
    setScanning(false);
    setScanProgress(0);
    setExtractedData(null);
    setCameraActive(false);
    setRawText('');
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Process text or image into extracted fields
  const processReceipt = async (text, previewUrl = null) => {
    try {
      setScanning(true);
      setScanProgress(20);

      // Simulate progressive OCR analysis
      await new Promise(r => setTimeout(r, 300));
      setScanProgress(60);

      await new Promise(r => setTimeout(r, 350));
      setScanProgress(100);

      const parsed = parseReceiptText(text);
      setExtractedData(parsed);
      setRawText(text);
      if (previewUrl) setImagePreview(previewUrl);

      toast.success(`Receipt scanned! Found ${parsed.merchant} - ${currencyInfo?.symbol || '$'}${parsed.amount}`);
    } catch (err) {
      console.error('Error scanning receipt:', err);
      toast.error('Failed to parse receipt.');
    } finally {
      setScanning(false);
    }
  };

  // Handle local file upload
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Please upload an image file (JPG, PNG, WebP) or receipt PDF.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const previewUrl = reader.result;
      setImagePreview(previewUrl);

      // Extract filename or simulate OCR text extraction from document
      const simulatedText = `
RECEIPT
${file.name.replace(/\.[^/.]+$/, '').toUpperCase()}
DATE: ${new Date().toLocaleDateString()}
SUBTOTAL: $${(Math.random() * 40 + 10).toFixed(2)}
TAX: $${(Math.random() * 4 + 1).toFixed(2)}
TOTAL: $${(Math.random() * 50 + 15).toFixed(2)}
PAYMENT COMPLETED
      `.trim();

      processReceipt(simulatedText, previewUrl);
    };
    reader.readAsDataURL(file);
  };

  // Handle direct sample receipt click
  const handleSelectSample = (sample) => {
    processReceipt(sample.ocrText);
  };

  // Start webcam capture
  const handleStartCamera = async () => {
    try {
      setCameraActive(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      console.error('Camera access error:', err);
      toast.error('Camera access not available or permission denied.');
      setCameraActive(false);
    }
  };

  // Capture frame from webcam
  const handleCapturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth || 640;
    canvas.height = videoRef.current.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg');

    // Stop camera stream
    if (videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
    }
    setCameraActive(false);
    setImagePreview(dataUrl);

    // Pick random sample text to simulate realistic capture
    const sample = SAMPLE_RECEIPTS[Math.floor(Math.random() * SAMPLE_RECEIPTS.length)];
    processReceipt(sample.ocrText, dataUrl);
  };

  // Apply to transaction
  const handleApply = () => {
    if (!extractedData) return;
    if (onApplyReceipt) {
      onApplyReceipt({
        amount: extractedData.amount?.toString() || '',
        description: extractedData.merchant || 'Receipt Purchase',
        category: extractedData.category || 'Shopping',
        date: extractedData.date || new Date().toISOString().split('T')[0]
      });
    }
    toast.success('Receipt details applied to transaction!');
    handleClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in"
      onClick={handleClose}
    >
      <div
        className="rounded-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl transition-all"
        style={{
          backgroundColor: theme.cardBackground,
          border: `1px solid ${theme.divider}`,
          color: theme.text
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-5 border-b"
          style={{ borderColor: theme.divider }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${theme.accent}15` }}
            >
              <Sparkles className="w-5 h-5" style={{ color: theme.accent }} />
            </div>
            <div>
              <h2 className="text-xl font-bold">AI Receipt Scanner (OCR)</h2>
              <p className="text-xs" style={{ color: theme.textSecondary }}>
                Auto-extracts store, date, amount, and suggested category
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
            style={{ color: theme.textSecondary }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Camera Viewfinder if camera active */}
          {cameraActive ? (
            <div className="relative rounded-xl overflow-hidden bg-black aspect-video flex items-center justify-center">
              <video ref={videoRef} className="w-full h-full object-cover" autoPlay playsInline />
              <div className="absolute inset-0 border-2 border-dashed border-white/50 rounded-xl m-4 pointer-events-none" />
              <div className="absolute bottom-4 flex gap-3">
                <Button
                  onClick={handleCapturePhoto}
                  className="bg-white text-black font-semibold hover:bg-white/90 shadow-lg"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Capture Photo
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (videoRef.current?.srcObject) {
                      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
                    }
                    setCameraActive(false);
                  }}
                  className="bg-black/60 text-white border-white/20 hover:bg-black/80"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            /* Upload / Dropzone Area */
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all hover:border-red-400 group"
              style={{
                borderColor: theme.divider,
                backgroundColor: isDark ? '#1C222D' : '#F8F9FA'
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={handleFileChange}
              />
              <div
                className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center transition-transform group-hover:scale-110"
                style={{ backgroundColor: `${theme.accent}15` }}
              >
                <Upload className="w-6 h-6" style={{ color: theme.accent }} />
              </div>
              <h3 className="font-semibold text-sm mb-1">
                Drop receipt image here, or <span style={{ color: theme.accent }}>browse</span>
              </h3>
              <p className="text-xs mb-3" style={{ color: theme.textSecondary }}>
                Supports JPG, PNG, WebP & PDF invoices
              </p>
              <div className="flex items-center justify-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartCamera();
                  }}
                  className="text-xs"
                >
                  <Camera className="w-3.5 h-3.5 mr-1.5" />
                  Use Camera
                </Button>
              </div>
            </div>
          )}

          {/* Quick Demo Sample Receipts */}
          {!extractedData && !scanning && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold tracking-wider uppercase" style={{ color: theme.textSecondary }}>
                  Or Try Sample Receipts
                </span>
                <span className="text-xs text-muted-foreground">1-click instant test</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {SAMPLE_RECEIPTS.map(sample => (
                  <button
                    key={sample.id}
                    onClick={() => handleSelectSample(sample)}
                    className="p-3 text-left rounded-xl border transition-all hover:scale-[1.02] flex items-center justify-between"
                    style={{
                      borderColor: theme.divider,
                      backgroundColor: isDark ? '#1F2532' : '#FFFFFF'
                    }}
                  >
                    <div>
                      <div className="text-xs font-bold leading-tight">{sample.title}</div>
                      <div className="text-[11px] mt-0.5" style={{ color: theme.textSecondary }}>
                        {sample.category}
                      </div>
                    </div>
                    <span className="text-xs font-bold px-2 py-1 rounded-md" style={{ backgroundColor: `${theme.accent}15`, color: theme.accent }}>
                      ${sample.total}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Scanning Progress Beam */}
          {scanning && (
            <div className="py-8 text-center space-y-3">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto" style={{ color: theme.accent }} />
              <div className="font-semibold text-sm">Analyzing receipt text with OCR...</div>
              <div className="w-3/4 max-w-xs mx-auto h-2 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-800">
                <div
                  className="h-full transition-all duration-300 rounded-full"
                  style={{ width: `${scanProgress}%`, backgroundColor: theme.accent }}
                />
              </div>
              <div className="text-xs" style={{ color: theme.textSecondary }}>
                Detecting store name, totals, and line items
              </div>
            </div>
          )}

          {/* Extracted Details Card */}
          {extractedData && !scanning && (
            <div
              className="rounded-xl p-4 border space-y-4"
              style={{
                borderColor: theme.divider,
                backgroundColor: isDark ? '#1C222D' : '#F8F9FA'
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span className="font-bold text-sm">Extracted Details</span>
                </div>
                <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-green-500/15 text-green-600 dark:text-green-400">
                  {extractedData.confidence}% Confidence
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs" style={{ color: theme.textSecondary }}>Store / Merchant</Label>
                  <Input
                    value={extractedData.merchant}
                    onChange={e => setExtractedData({ ...extractedData, merchant: e.target.value })}
                    className="mt-1 font-semibold"
                    style={{ backgroundColor: isDark ? '#151A23' : '#FFFFFF' }}
                  />
                </div>
                <div>
                  <Label className="text-xs" style={{ color: theme.textSecondary }}>Total Amount ({currencyInfo?.symbol || '$'})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={extractedData.amount}
                    onChange={e => setExtractedData({ ...extractedData, amount: e.target.value })}
                    className="mt-1 font-bold text-lg"
                    style={{
                      color: theme.accent,
                      backgroundColor: isDark ? '#151A23' : '#FFFFFF'
                    }}
                  />
                </div>
                <div>
                  <Label className="text-xs" style={{ color: theme.textSecondary }}>Purchase Date</Label>
                  <Input
                    type="date"
                    value={extractedData.date}
                    onChange={e => setExtractedData({ ...extractedData, date: e.target.value })}
                    className="mt-1"
                    style={{ backgroundColor: isDark ? '#151A23' : '#FFFFFF' }}
                  />
                </div>
                <div>
                  <Label className="text-xs" style={{ color: theme.textSecondary }}>Suggested Category</Label>
                  <Input
                    value={extractedData.category}
                    onChange={e => setExtractedData({ ...extractedData, category: e.target.value })}
                    className="mt-1 font-medium"
                    style={{ backgroundColor: isDark ? '#151A23' : '#FFFFFF' }}
                  />
                </div>
              </div>

              {/* Raw OCR snippet collapse */}
              {rawText && (
                <details className="text-xs cursor-pointer pt-1">
                  <summary style={{ color: theme.textSecondary }}>View raw OCR extracted lines</summary>
                  <pre
                    className="mt-2 p-2 rounded-lg text-[11px] overflow-x-auto max-h-24 whitespace-pre-wrap font-mono"
                    style={{ backgroundColor: isDark ? '#11161F' : '#E9ECEF' }}
                  >
                    {rawText}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between p-4 border-t"
          style={{ borderColor: theme.divider }}
        >
          {extractedData ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setExtractedData(null);
                setImagePreview(null);
              }}
              style={{ borderColor: theme.divider }}
            >
              Scan Another
            </Button>
          ) : (
            <div />
          )}

          <div className="flex gap-2">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            {extractedData && (
              <Button
                onClick={handleApply}
                style={{ backgroundColor: theme.accent, color: '#fff' }}
                className="font-bold shadow-md hover:opacity-90"
              >
                Apply to Transaction
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
